/**
 * Announcing a PRE-REQUEST script failure.
 *
 * Surface choice, and the whole reason this module exists: a failing
 * pre-request script aborts the send, and RequestBuilder reported that abort
 * with `showToast`. Popup toasts are globally disabled
 * (`components/ui/Toast.js` sets `POPUP_TOASTS_ENABLED = false` and `addToast`
 * early-returns), so the message reached nobody — pressing Send did nothing at
 * all, with no response and no error. That is indistinguishable from a broken
 * app, and it is how an imported Postman collection's token script presented
 * (owner's friend, 22.09.2026).
 *
 * A POST-RESPONSE script needs none of this: its errors already land in the
 * Tests panel via `setTestScriptErrors`, which is a real surface and stays put.
 *
 * The live surface is `useNotificationStore` (rendered by NotificationContainer
 * through ModalStack) — the same one `services/syncRejections.js` reaches for,
 * and for the same reason.
 */
import { useNotificationStore } from '../store/notificationStore';

const TITLE = 'Request not sent';

/**
 * A script error can be a stack-ish blob. The first line is the part a human
 * reads; the rest belongs in the console, not in a notification card.
 */
function firstLine(message) {
  return String(message || '').split('\n')[0].trim();
}

/**
 * Why the send was abandoned, in one sentence the user can act on.
 *
 * `pm.sendRequest` gets its own wording only while it is genuinely unsupported
 * — the raw sandbox message names the call but not what to do instead.
 */
export function preRequestFailureMessage(error) {
  const text = firstLine(error);
  if (!text) return 'The pre-request script failed, so nothing was sent.';
  return `${text} Nothing was sent.`;
}

/**
 * Announce an aborted send.
 *
 * @param {string|Error} error The first sandbox error for the run.
 * @returns {string} the announced message, so callers and tests can assert it.
 */
export function announcePreRequestFailure(error) {
  const message = preRequestFailureMessage(error?.message ?? error);
  useNotificationStore.getState().addNotification({
    type: 'error',
    title: TITLE,
    message,
    // duration 0 disables the auto-dismiss timer. There is no retry and no
    // repair path here — telling the user IS the repair — so it must not
    // evaporate while they are looking at the script they need to fix.
    duration: 0,
  });
  return message;
}

/**
 * Announce a failed unattended OAuth2 renewal.
 *
 * Softer than the above on purpose: the send still happens, carrying the stale
 * token, so the server gets to answer. The user needs to know why a 401 is
 * about to appear, not to be told nothing was sent.
 */
export function announceOAuthRenewalFailure(error) {
  const detail = firstLine(error?.message ?? error);
  const message = detail
    ? `Could not renew the OAuth2 access token: ${detail} The request was sent with the old one.`
    : 'Could not renew the OAuth2 access token. The request was sent with the old one.';
  useNotificationStore.getState().addNotification({
    type: 'warning',
    title: 'Token not renewed',
    message,
    duration: 0,
  });
  return message;
}

export default announcePreRequestFailure;
